import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Stethoscope, Loader2, FileDown, Sparkles, CheckSquare, Square, FileCode, Package } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Diagnostico() {
    const [params] = useSearchParams();
    const [procs, setProcs] = useState([]);
    const [selId, setSelId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [report, setReport] = useState(null); // {report, generated_by, generated_at, processes, model}
    const [phase, setPhase] = useState("");

    const load = useCallback(async () => {
        try {
            const r = await api.get("/processes");
            setProcs(r.data || []);
        } catch (e) { toast.error("Não foi possível carregar seus processos."); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Auto-execução vindo do botão "Analisar com IA" em Meus Processos
    useEffect(() => {
        const pid = params.get("process");
        if (pid && procs.length && !report && !busy) {
            if (procs.some((p) => p.id === pid)) { generate(pid); }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [procs]);

    const select = (id) => setSelId((cur) => (cur === id ? null : id));

    const generate = async (id) => {
        const chosen = id || selId;
        if (!chosen) { toast.error("Escolha um processo para diagnosticar."); return; }
        setBusy(true); setReport(null); setPhase("Enviando o processo para análise…");
        try {
            const start = await api.post("/diagnostico/generate", { process_ids: [chosen] });
            const jobId = start.data.job_id;
            setPhase("Preparando o diagnóstico…");
            let tries = 0;
            const poll = async () => {
                tries += 1;
                const s = await api.get(`/diagnostico/status/${jobId}`);
                if (s.data.progress) setPhase(s.data.progress);
                if (s.data.status === "done") { setReport(s.data); setBusy(false); setPhase(""); toast.success("Diagnóstico gerado."); return; }
                if (s.data.status === "error") { toast.error(s.data.error || "Falha ao gerar diagnóstico."); setBusy(false); setPhase(""); return; }
                if (tries > 90) { toast.error("A análise está demorando mais que o esperado. Tente novamente."); setBusy(false); setPhase(""); return; }
                setTimeout(poll, 4000);
            };
            setTimeout(poll, 4000);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Não foi possível iniciar o diagnóstico.");
            setBusy(false); setPhase("");
        }
    };

    const printPdf = () => window.print();

    const exportLatex = async (fmt) => {
        if (!report?.job_id) { toast.error("Gere o diagnóstico antes de exportar."); return; }
        try {
            const res = await api.get(`/diagnostico/export/${report.job_id}?fmt=${fmt}`, { responseType: "blob", timeout: 60000 });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = fmt === "zip" ? "diagnostico_anm_overleaf.zip" : "diagnostico_anm.tex";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("Não foi possível exportar o arquivo."); }
    };

    return (
        <div className="space-y-4" data-testid="diagnostico-page">
            <div className="flex items-start gap-3 no-print">
                <Stethoscope className="w-7 h-7 text-mineral mt-1" />
                <div>
                    <h1 className="font-heading text-2xl font-black text-slate-900">Diagnóstico Inteligente ANM</h1>
                    <p className="text-sm text-slate-500">Análise técnica e objetiva de <b>um processo por vez</b>, com base exclusivamente nos dados oficiais já disponíveis na plataforma (Cadastro Mineiro/SIGMINE e atos do DOU/INLABS já coletados). Em poucos segundos você vê a <b>situação atual</b>, os <b>prazos e obrigações identificados</b> e os <b>próximos passos</b> perante a ANM. Escolha um processo, gere o diagnóstico e veja o resultado; para analisar outro, basta escolher novamente. Exporte em PDF, LaTeX (.tex) ou ZIP (Overleaf). A IA apenas organiza os dados existentes — nunca inventa atos, datas ou prazos.</p>
                </div>
            </div>

            {!report && (
                <div className="bg-white border border-slate-200 rounded-md shadow-sm no-print" data-testid="diag-selection">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                        <span className="text-sm font-semibold text-slate-600">Escolha um processo para diagnosticar ({procs.length})</span>
                        <button onClick={() => generate()} disabled={busy || !selId} data-testid="diag-generate"
                            className="bg-mineral hover:bg-mineralHover text-white rounded-sm px-5 py-2 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60 transition-colors">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            GERAR DIAGNÓSTICO INTELIGENTE
                        </button>
                    </div>
                    {loading ? (
                        <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                    ) : procs.length === 0 ? (
                        <div className="p-10 text-center text-slate-500">Você ainda não tem processos cadastrados em Meus Processos.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="bg-slate-50 text-left text-xs uppercase text-slate-500"><th className="py-2.5 px-4"></th><th className="py-2.5 px-4">Processo</th><th className="py-2.5 px-4">Titular</th><th className="py-2.5 px-4">Substância</th><th className="py-2.5 px-4">Fase</th><th className="py-2.5 px-4">Situação ANM</th><th className="py-2.5 px-4">Última atualização</th></tr></thead>
                                <tbody>{procs.map((p) => (
                                    <tr key={p.id} onClick={() => select(p.id)} data-testid={`diag-row-${p.id}`} className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${selId === p.id ? "bg-mineral/5" : ""}`}>
                                        <td className="py-2.5 px-4">{selId === p.id ? <CheckSquare className="w-4 h-4 text-mineral" /> : <Square className="w-4 h-4 text-slate-300" />}</td>
                                        <td className="py-2.5 px-4 font-semibold text-slate-800">{p.process_number}</td>
                                        <td className="py-2.5 px-4 text-slate-500">{p.holder || "—"}</td>
                                        <td className="py-2.5 px-4 text-slate-500">{p.substance || "—"}</td>
                                        <td className="py-2.5 px-4 text-slate-500">{p.anm_phase || p.phase || "—"}</td>
                                        <td className="py-2.5 px-4 text-slate-500">{p.anm_status || "—"}</td>
                                        <td className="py-2.5 px-4 text-slate-400 text-xs">{p.anm_last_sync ? new Date(p.anm_last_sync).toLocaleDateString("pt-BR") : "—"}</td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {busy && !report && (
                <div className="bg-white border border-slate-200 rounded-md p-10 text-center shadow-sm" data-testid="diag-loading">
                    <Loader2 className="w-10 h-10 text-mineral animate-spin mx-auto" />
                    <p className="mt-4 font-semibold text-slate-700">Gerando o diagnóstico do processo…</p>
                    <p className="text-sm text-slate-500 mt-1">{phase} Normalmente leva alguns segundos. Mantenha esta aba aberta.</p>
                </div>
            )}

            {report && (
                <div className="bg-white border border-slate-200 rounded-md shadow-sm" data-testid="diag-report">
                    <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-slate-100 no-print">
                        <div className="text-xs text-slate-500">
                            Gerado por <b>{report.generated_by}</b> em {report.generated_at} · {report.processes?.length} processo(s) · modelo {report.model}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => { setReport(null); }} data-testid="diag-new" className="border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-sm px-4 py-2 text-sm font-medium">Novo diagnóstico</button>
                            <button onClick={printPdf} data-testid="diag-pdf" className="bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-4 py-2 text-sm font-semibold inline-flex items-center gap-2"><FileDown className="w-4 h-4" />PDF</button>
                            <button onClick={() => exportLatex("tex")} data-testid="diag-latex" className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm px-4 py-2 text-sm font-semibold inline-flex items-center gap-2"><FileCode className="w-4 h-4" />LaTeX (.tex)</button>
                            <button onClick={() => exportLatex("zip")} data-testid="diag-overleaf" className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm px-4 py-2 text-sm font-semibold inline-flex items-center gap-2"><Package className="w-4 h-4" />ZIP Overleaf</button>
                        </div>
                    </div>
                    <div className="p-6 print-area">
                        <div className="hidden print:block mb-4">
                            <h1 className="font-heading text-xl font-black text-slate-900">GeoMine Manager — Diagnóstico Inteligente ANM</h1>
                            <p className="text-xs text-slate-500">Gerado por {report.generated_by} em {report.generated_at} · Processos: {report.processes?.join(", ")}</p>
                        </div>
                        <article className="diag-markdown max-w-none text-sm text-slate-800 leading-relaxed">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.report}</ReactMarkdown>
                        </article>
                    </div>
                </div>
            )}
        </div>
    );
}
